'use client'

import { useState, useMemo, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'

interface Workshop {
  id: string
  slug: string
  title: string
  date: string
  isActive: boolean
}

// Question data
const questions = [
  // Immersion & Flow (5 questions)
  { q: 'I was aware of my own internal flow of experiencing.', cat: 'immersion', reverse: false },
  { q: 'The interaction between my client and I felt flowing and rhythmic.', cat: 'immersion', reverse: false },
  { q: "There were moments when I was so immersed in my client's experience that I lost a sense of time and space.", cat: 'immersion', reverse: false },
  { q: 'I was fully in the moment during this session.', cat: 'immersion', reverse: false },
  { q: "I felt fully immersed in my client's experience and yet still centered within myself.", cat: 'immersion', reverse: false },
  // Attunement & Connection (5 questions)
  { q: 'I felt a sense of deep appreciation and respect for my client as a person.', cat: 'attunement', reverse: false },
  { q: "I felt alert and attuned to the nuances and subtleties of my client's experience.", cat: 'attunement', reverse: false },
  { q: 'My responses were guided by feelings, words, images, or intuitions that emerged from being with my client.', cat: 'attunement', reverse: false },
  { q: 'I felt in sync with my client in a way that let me sense what they were experiencing.', cat: 'attunement', reverse: false },
  { q: "I felt genuinely interested in my client's experience.", cat: 'attunement', reverse: false },
  // Emotional Availability (4 questions, some reverse-scored)
  { q: 'I was able to set aside my own demands and worries to be with my client.', cat: 'emotional', reverse: false },
  { q: 'I felt distant or disconnected from my client.', cat: 'emotional', reverse: true },
  { q: 'There were moments when my outward response was different from how I actually felt inside.', cat: 'emotional', reverse: true },
  { q: 'I felt a distance or emotional barrier between my client and myself.', cat: 'emotional', reverse: true },
  // Focus & Sustained Attention (7 questions, all reverse-scored)
  { q: 'I felt tired or bored.', cat: 'focus', reverse: true },
  { q: 'I found it difficult to listen to my client.', cat: 'focus', reverse: true },
  { q: 'Time seemed to really drag.', cat: 'focus', reverse: true },
  { q: 'I found it difficult to concentrate.', cat: 'focus', reverse: true },
  { q: 'I felt impatient or critical.', cat: 'focus', reverse: true },
  { q: "I couldn't wait for the session to be over.", cat: 'focus', reverse: true },
  { q: 'My thoughts sometimes drifted away from what was happening in the moment.', cat: 'focus', reverse: true },
]

const catMeta: Record<string, { emoji: string; name: string; desc: string }> = {
  immersion: { emoji: '🌊', name: 'Immersion & Flow', desc: 'How deeply you entered the shared experience of the session.' },
  attunement: { emoji: '🎯', name: 'Attunement & Connection', desc: "How sensitively you tracked your client's inner world." },
  emotional: { emoji: '💛', name: 'Emotional Availability', desc: 'How open and congruent you were emotionally.' },
  focus: { emoji: '🔍', name: 'Focus & Sustained Attention', desc: 'How steady your attention and concentration remained.' }
}

type SlideType =
  | { type: 'section'; cat: string; questions: Array<{ index: number; q: string; reverse: boolean }> }

// Build slides: sections (with all questions) + notes
function buildSlides(): SlideType[] {
  const slides: SlideType[] = []

  const categories = ['immersion', 'attunement', 'emotional', 'focus']
  categories.forEach(cat => {
    const catQuestions = questions
      .map((q, i) => ({ ...q, index: i }))
      .filter(q => q.cat === cat)
      .map(q => ({ index: q.index, q: q.q, reverse: q.reverse }))

    slides.push({ type: 'section', cat, questions: catQuestions })
  })

  return slides
}

const slides = buildSlides()
const totalQuestions = questions.length

export default function TherapeuticPresencePage() {
  const router = useRouter()
  const [hasAccess, setHasAccess] = useState(false)
  const [activeWorkshop, setActiveWorkshop] = useState<Workshop | null>(null)

  // Fetch active "use-of-self" workshop
  useEffect(() => {
    async function fetchWorkshop() {
      try {
        const res = await fetch('/api/workshops/use-of-self')
        if (res.ok) {
          const data = await res.json()
          // Only set if workshop is in the future
          if (data && new Date(data.date) > new Date()) {
            setActiveWorkshop(data)
          }
        }
      } catch (err) {
        console.error('Failed to fetch workshop:', err)
      }
    }
    fetchWorkshop()
  }, [])
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [currentSlide, setCurrentSlide] = useState(0)
  const [currentQuestionInSection, setCurrentQuestionInSection] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(() => new Array(totalQuestions).fill(null))
  const [showResults, setShowResults] = useState(false)
  const [animating, setAnimating] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [inlineNotes, setInlineNotes] = useState('')
  const [isSavingInlineResults, setIsSavingInlineResults] = useState(false)
  const [inlineSaveError, setInlineSaveError] = useState<string | null>(null)

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
  const canAccessTool = firstName.trim() && isValidEmail(email)

  // Get current section index (0-3) based on current slide
  const currentSectionIndex = useMemo(() => {
    const slide = slides[currentSlide]
    if (!slide) return -1
    if (slide.type === 'section') {
      const catOrder = ['immersion', 'attunement', 'emotional', 'focus']
      return catOrder.indexOf(slide.cat)
    }
    return -1
  }, [currentSlide])

  const selectAnswer = (qIdx: number, val: number) => {
    const newAnswers = [...answers]
    newAnswers[qIdx] = val
    setAnswers(newAnswers)

    // Auto-advance to next question after a brief delay
    const slide = slides[currentSlide]
    if (slide?.type === 'section') {
      const totalInSection = slide.questions.length
      // Always advance - even past the last question to show completion screen
      if (currentQuestionInSection < totalInSection) {
        setAnimating(true)
        setTimeout(() => {
          setCurrentQuestionInSection(prev => prev + 1)
          setAnimating(false)
        }, 300)
      }
    }
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const nextSlide = () => {
    if (animating) return

    const slide = slides[currentSlide]
    if (!slide) return

    // Check if this is the last section (focus) and we should save
    const isLastSection = currentSlide === slides.length - 1

    if (isLastSection) {
      // Save results and redirect to results page
      if (!hasSaved) {
        setHasSaved(true)
        setAnimating(true)

        const answersObject = answers.reduce((acc, val, idx) => {
          if (val !== null) acc[idx] = val
          return acc
        }, {} as Record<number, number>)

        fetch('/api/tools/self-assessment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'therapeutic-presence',
            firstName,
            email,
            answers: answersObject,
            notes: null,
          }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.id) {
              router.push(`/tools/therapeutic-presence/results/${data.id}`)
            } else {
              // Fallback to showing results inline if save fails
              setShowResults(true)
              setAnimating(false)
              scrollToTop()
            }
          })
          .catch(() => {
            // Fallback to showing results inline if save fails
            setShowResults(true)
            setAnimating(false)
            scrollToTop()
          })
      }
      return
    }

    setAnimating(true)
    setTimeout(() => {
      setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1))
      setCurrentQuestionInSection(0) // Start at first question
      setAnimating(false)
      scrollToTop()
    }, 150)
  }

  const goBack = () => {
    if (animating) return

    const slide = slides[currentSlide]

    // If we're in a section and not on the first question, go back within the section
    if (slide?.type === 'section' && currentQuestionInSection > 0) {
      setAnimating(true)
      setTimeout(() => {
        setCurrentQuestionInSection(prev => prev - 1)
        setAnimating(false)
      }, 150)
      return
    }

    // Otherwise go to previous slide
    if (currentSlide > 0) {
      setAnimating(true)
      setTimeout(() => {
        const newSlide = currentSlide - 1
        setCurrentSlide(newSlide)
        // Set to last question of previous section
        const prevSection = slides[newSlide]
        if (prevSection?.type === 'section') {
          setCurrentQuestionInSection(prevSection.questions.length - 1)
        } else {
          setCurrentQuestionInSection(0)
        }
        setAnimating(false)
        scrollToTop()
      }, 150)
    }
  }

  // Check if can go back
  const canGoBack = () => {
    return currentSlide > 0 || currentQuestionInSection > 0
  }

  const resetTool = () => {
    setAnswers(new Array(totalQuestions).fill(null))
    setCurrentSlide(0)
    setCurrentQuestionInSection(0)
    setShowResults(false)
    scrollToTop()
  }

  // Calculate scores
  const scores = useMemo(() => {
    const catScores = {
      immersion: { total: 0, count: 0 },
      attunement: { total: 0, count: 0 },
      emotional: { total: 0, count: 0 },
      focus: { total: 0, count: 0 },
    }

    questions.forEach((q, i) => {
      const answer = answers[i]
      if (answer !== null && answer !== undefined) {
        // Reverse scored: subtract from 8
        const scoreVal = q.reverse ? 8 - answer : answer
        const catData = catScores[q.cat as keyof typeof catScores]
        if (catData) {
          catData.total += scoreVal
          catData.count += 1
        }
      }
    })

    return {
      immersion: catScores.immersion.count > 0 ? catScores.immersion.total / catScores.immersion.count : 0,
      attunement: catScores.attunement.count > 0 ? catScores.attunement.total / catScores.attunement.count : 0,
      emotional: catScores.emotional.count > 0 ? catScores.emotional.total / catScores.emotional.count : 0,
      focus: catScores.focus.count > 0 ? catScores.focus.total / catScores.focus.count : 0,
    }
  }, [answers])

  const overallScore = useMemo(() => {
    const validScores = Object.values(scores).filter(s => s > 0)
    if (validScores.length === 0) return 0
    return validScores.reduce((a, b) => a + b, 0) / validScores.length
  }, [scores])

  // Render current slide
  const renderSlide = () => {
    const slide = slides[currentSlide]
    if (!slide) return null

    if (slide.type === 'section') {
      const meta = catMeta[slide.cat] || { emoji: '', name: '', desc: '' }
      const totalInSection = slide.questions.length
      const question = slide.questions[currentQuestionInSection]
      const savedVal = question ? answers[question.index] : null
      const answeredInSection = slide.questions.filter(q => answers[q.index] !== null).length

      // Show completion screen when we've moved past the last question
      const showingCompletionScreen = currentQuestionInSection >= totalInSection && answeredInSection === totalInSection

      // Get next section info for completion screen
      const sectionOrder = ['immersion', 'attunement', 'emotional', 'focus']
      const currentSectionIdx = sectionOrder.indexOf(slide.cat)
      const nextSectionCat = sectionOrder[currentSectionIdx + 1]
      const nextMeta = nextSectionCat ? catMeta[nextSectionCat] : null

      // Section complete state
      if (showingCompletionScreen) {
        return (
          <div className="text-center py-4">
            {/* Completed section */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-8 h-8 bg-[#4A7C7C] rounded-full flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="text-[#4A7C7C] font-medium">{meta.name}</span>
            </div>

            {nextMeta ? (
              <>
                {/* Next section preview */}
                <div className="bg-[#F8F5F2] rounded-xl p-6 text-left">
                  <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-3">Up next</p>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{nextMeta.emoji}</div>
                    <div>
                      <p className="font-semibold text-[#2D2D2D] mb-1">{nextMeta.name}</p>
                      <p className="text-sm text-[#7A7A7A] leading-relaxed">{nextMeta.desc}</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-[#E8F2F2] rounded-xl p-6">
                <p className="text-2xl mb-2">✨</p>
                <p className="font-medium text-[#2E5B5B] mb-1">All done</p>
                <p className="text-sm text-[#4A7C7C]">Your presence snapshot is ready.</p>
              </div>
            )}
          </div>
        )
      }

      // Current question
      if (question) {
        const isFirstQuestion = currentSlide === 0 && currentQuestionInSection === 0

        return (
          <div className="text-center">
            {/* Instructions - only show on first question */}
            {isFirstQuestion && (
              <div className="mb-6 pb-6 border-b border-[#E8E0D8]">
                <p className="text-[#2D2D2D] leading-relaxed mb-2">
                  Bring to mind a recent client session. For each statement, rate how much it reflects your experience.
                </p>
                <p className="text-[#7A7A7A] text-sm">
                  Don't overthink it. First instinct is usually most honest.
                </p>
              </div>
            )}

            {/* Section badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8F2F2] rounded-full text-sm font-medium text-[#2E5B5B] mb-6">
              {meta.emoji} {meta.name}
            </div>

            <div className={`transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`}>
              {/* Question progress */}
              <div className="flex justify-center gap-1.5 mb-6">
                {slide.questions.map((q, i) => (
                  <div
                    key={q.index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      answers[q.index] !== null ? 'bg-[#4A7C7C]' :
                      i === currentQuestionInSection ? 'bg-[#4A7C7C]/40' : 'bg-[#E8E0D8]'
                    }`}
                  />
                ))}
              </div>

              {/* Question text */}
              <p className="text-[#2D2D2D] text-lg leading-relaxed mb-8 max-w-[400px] mx-auto">
                {question.q}
              </p>

              {/* Answer buttons */}
              <div className="flex gap-2 justify-center max-w-[360px] mx-auto">
                {[1, 2, 3, 4, 5, 6, 7].map((val) => (
                  <button
                    key={val}
                    onClick={() => selectAnswer(question.index, val)}
                    className={`w-11 h-11 rounded-full font-medium text-sm transition-all ${
                      savedVal === val
                        ? 'bg-[#4A7C7C] text-white scale-110'
                        : 'bg-[#F8F5F2] text-[#7A7A7A] hover:bg-[#E8F2F2] hover:text-[#2E5B5B] hover:scale-105'
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[#aaa] max-w-[360px] mx-auto mt-2 px-1">
                <span>Not at all</span>
                <span>Completely</span>
              </div>
            </div>
          </div>
        )
      }

      return null
    }

    return null
  }

  // Check if can proceed
  const canProceed = () => {
    const slide = slides[currentSlide]
    if (!slide) return false
    if (slide.type === 'section') {
      // Show continue button only when on the completion screen (past last question and all answered)
      const totalInSection = slide.questions.length
      const allAnswered = slide.questions.every(q => answers[q.index] !== null)
      return currentQuestionInSection >= totalInSection && allAnswered
    }
    return false
  }

  // Get interpretation for a score
  const getInterpretation = (score: number) => {
    if (score >= 5.5) return { level: 'high', color: '#4A7C7C', text: 'Strong presence in this area.' }
    if (score >= 4) return { level: 'mid', color: '#D4A574', text: 'Moderate presence — some room for attention.' }
    return { level: 'low', color: '#C4938A', text: 'This area may need more attention.' }
  }

  // Results view
  if (showResults) {
    return (
      <div className="min-h-screen bg-[#F5F0EB] font-sans -mt-20">
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-[600px] mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl text-[#2D2D2D] mb-3">Your Presence Snapshot</h1>
              <p className="text-[#7A7A7A]">
                Based on your responses, here's how your therapeutic presence looked in that session.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6">
              {/* Overall Score */}
              <div className="text-center mb-8 pb-8 border-b border-[#E8E0D8]">
                <div className="text-5xl font-serif text-[#4A7C7C] mb-2">
                  {overallScore.toFixed(1)}
                </div>
                <p className="text-sm text-[#7A7A7A]">Overall Presence Score (out of 7)</p>
              </div>

              {/* Category Scores */}
              <div className="space-y-6">
                {(['immersion', 'attunement', 'emotional', 'focus'] as const).map((cat) => {
                  const score = scores[cat]
                  const meta = catMeta[cat] || { emoji: '', name: '', desc: '' }
                  const interp = getInterpretation(score)
                  const percent = (score / 7) * 100

                  return (
                    <div key={cat}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{meta.emoji}</span>
                          <span className="font-medium text-[#2D2D2D]">{meta.name}</span>
                        </div>
                        <span className="font-semibold text-[#2D2D2D]">{score.toFixed(1)}</span>
                      </div>
                      <div className="h-2 bg-[#E8E0D8] rounded-full overflow-hidden mb-2">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${percent}%`, backgroundColor: interp.color }}
                        />
                      </div>
                      <p className="text-xs text-[#7A7A7A]">{interp.text}</p>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Insight Card */}
            <div className="bg-[#E8F2F2] rounded-2xl p-6 mb-6">
              <h3 className="font-serif text-lg text-[#2E5B5B] mb-3">What this might mean</h3>
              <div className="text-sm text-[#2E5B5B] leading-relaxed space-y-3">
                {/* Exceptional presence - all categories high */}
                {scores.immersion >= 5.5 && scores.attunement >= 5.5 && scores.emotional >= 5.5 && scores.focus >= 5.5 && (
                  <p>You were deeply present across all dimensions — grounded in yourself while fully available to your client. Notice what conditions supported this.</p>
                )}

                {/* Strong flow state */}
                {scores.immersion >= 5 && scores.focus >= 5 && !(scores.immersion >= 5.5 && scores.attunement >= 5.5 && scores.emotional >= 5.5 && scores.focus >= 5.5) && (
                  <p>You showed strong presence across immersion and focus — that sense of being "in the zone" where time flows naturally.</p>
                )}

                {/* Attunement high but emotional low - depletion signal */}
                {scores.attunement >= 5 && scores.emotional < 4 && (
                  <p>Your attunement is strong, but your emotional availability dipped. This can signal early depletion — you're tracking your client well, but your own resources may be running low.</p>
                )}

                {/* Focus low - concentration issues */}
                {scores.focus < 4 && (
                  <p>Focus and sustained attention scored lower. Mind-wandering is normal — the question is what's pulling your attention away.</p>
                )}

                {/* Immersion low - disconnection */}
                {scores.immersion < 4 && scores.focus >= 4 && (
                  <p>Immersion scored lower while focus held steady. You may have been "doing the work" without fully entering the shared experience.</p>
                )}

                {/* Attunement low - tracking difficulty */}
                {scores.attunement < 4 && (
                  <p>Attunement — sensing into your client's inner world — was harder this session. Sometimes this reflects the client's own difficulty being reached. Sometimes it's us.</p>
                )}

                {/* Emotional availability low (standalone) */}
                {scores.emotional < 4 && scores.attunement >= 4 && (
                  <p>Emotional availability was limited. You may have felt a barrier between you and your client, or noticed your outward responses not matching your inner experience.</p>
                )}

                {/* Solid overall - default message */}
                {overallScore >= 4 && overallScore < 5.5 && scores.focus >= 4 && scores.attunement >= 4 && scores.emotional >= 4 && scores.immersion >= 4 && (
                  <p>Your presence was steady across all dimensions. The value of this tool is in tracking patterns over time — notice how your presence shifts across different sessions.</p>
                )}

                {/* Lower overall - information, not judgment */}
                {overallScore < 4 && (
                  <p>These scores are information, not a verdict. Presence naturally fluctuates — it varies by session, by day, by what's happening in your life.</p>
                )}
              </div>
            </div>

            {/* Reflection Questions - Hybrid: universal + targeted based on scores */}
            <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
              <h3 className="font-medium text-[#2D2D2D] mb-3">Questions to sit with</h3>
              <ul className="text-sm text-[#7A7A7A] space-y-2">
                {/* Universal question */}
                <li>• What was going on in your life before this session?</li>

                {/* Targeted questions based on lower scores */}
                {scores.focus < 4 && (
                  <li>• If your mind wandered, what was it drawn to?</li>
                )}
                {scores.emotional < 4 && (
                  <li>• Was there something that felt hard to express in the room?</li>
                )}
                {scores.attunement < 4 && (
                  <li>• What might have made it harder to sense into your client's world?</li>
                )}
                {scores.immersion < 4 && (
                  <li>• What might have been keeping you at the edges of the session?</li>
                )}

                {/* If all scores are strong, offer a strength-based question */}
                {scores.focus >= 4 && scores.emotional >= 4 && scores.attunement >= 4 && scores.immersion >= 4 && (
                  <li>• What helped you be so present today?</li>
                )}

                {/* Universal closing question */}
                <li>• What would help you arrive more fully next time?</li>
              </ul>
            </div>

            {/* Notes Section */}
            <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E8E0D8]">
              <h3 className="font-medium text-[#2D2D2D] mb-1">Your notes</h3>
              <p className="text-xs text-[#aaa] mb-3">Just for you — not shared with anyone.</p>
              <textarea
                value={inlineNotes}
                onChange={(e) => setInlineNotes(e.target.value)}
                placeholder="What stood out? What do you want to remember about this session?"
                className="w-full min-h-[100px] p-3 border border-[#E8E0D8] rounded-xl text-sm text-[#2D2D2D] placeholder-[#bbb] outline-none focus:border-[#4A7C7C] resize-y"
              />
              {inlineSaveError && (
                <p className="text-xs text-red-500 mt-2">{inlineSaveError}</p>
              )}
              <button
                onClick={async () => {
                  setIsSavingInlineResults(true)
                  setInlineSaveError(null)
                  try {
                    const answersObject = answers.reduce((acc, val, idx) => {
                      if (val !== null) acc[idx] = val
                      return acc
                    }, {} as Record<number, number>)

                    const res = await fetch('/api/tools/self-assessment', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        type: 'therapeutic-presence',
                        firstName,
                        email,
                        answers: answersObject,
                        notes: inlineNotes || null,
                      }),
                    })
                    const data = await res.json()
                    if (data.id) {
                      router.push(`/tools/therapeutic-presence/results/${data.id}`)
                    } else {
                      setInlineSaveError('Failed to save. Please try again.')
                    }
                  } catch {
                    setInlineSaveError('Failed to save. Please try again.')
                  } finally {
                    setIsSavingInlineResults(false)
                  }
                }}
                disabled={isSavingInlineResults}
                className="mt-3 px-4 py-2 bg-[#4A7C7C] text-white text-sm rounded-full hover:bg-[#2E5B5B] transition-colors disabled:opacity-50"
              >
                {isSavingInlineResults ? 'Saving...' : 'Save Results & Notes'}
              </button>
            </div>

            {/* Workshop CTA - only show when there's an active workshop */}
            {activeWorkshop && (
              <div className="bg-gradient-to-br from-[#4A7C7C] to-[#2E5B5B] rounded-2xl p-8 text-center">
                <h3 className="font-serif text-xl text-white mb-2">Bring this to the workshop</h3>
                <p className="text-white/80 text-sm mb-6">
                  Join us on {new Date(activeWorkshop.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/New_York' })} to explore your patterns with other therapists and build a sustainable self-monitoring practice.
                </p>
                <Link
                  href="/workshops/use-of-self"
                  className="inline-flex items-center gap-2 bg-white text-[#4A7C7C] px-6 py-3 rounded-full font-semibold transition-all hover:-translate-y-0.5 shadow-lg"
                >
                  Save Your Spot — It's Free
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Reset */}
            <div className="text-center mt-8">
              <button
                onClick={resetTool}
                className="text-sm text-[#7A7A7A] hover:text-[#4A7C7C] transition-colors"
              >
                Start over with a different session
              </button>
              <p className="text-[10px] text-[#bbb] mt-6">
                Based on the Therapeutic Presence Inventory by Dr. Shari Geller
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Gate screen - collect name and email first
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#F5F0EB] font-sans -mt-20 pt-28">
        <div className="max-w-[560px] mx-auto px-6 py-16">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#E8F2F2] rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5">🧘</div>
            <h1 className="font-serif text-3xl text-[#2D2D2D] mb-3">Therapeutic Presence Self-Reflection</h1>
            <p className="text-[#7A7A7A] leading-relaxed max-w-[400px] mx-auto mb-3">
              Reflect on a recent session and notice how your presence showed up.
            </p>
            <p className="text-[#999] text-sm">21 questions · ~5 minutes</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-[0_4px_30px_rgba(0,0,0,0.08)]">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">
                  First name
                </label>
                <input
                  type="text"
                  placeholder="Your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#4A7C7C] focus:ring-2 focus:ring-[#4A7C7C]/15 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#2D2D2D] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-lg bg-white text-[#2D2D2D] focus:border-[#4A7C7C] focus:ring-2 focus:ring-[#4A7C7C]/15 outline-none"
                />
                <p className="text-xs text-[#7A7A7A] mt-1.5">We'll send your results here</p>
              </div>

              <button
                onClick={() => {
                  if (canAccessTool) {
                    setHasAccess(true)
                    setCurrentSlide(0) // Start at intro/instructions
                    setCurrentQuestionInSection(0)
                  }
                }}
                disabled={!canAccessTool}
                className={`w-full py-3.5 rounded-full font-bold transition-all ${
                  canAccessTool
                    ? 'bg-[#4A7C7C] hover:bg-[#2E5B5B] text-white shadow-[0_4px_15px_rgba(74,124,124,0.3)] hover:-translate-y-0.5'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Start Reflection →
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-[#7A7A7A] mt-6">
            Inspired by the Therapeutic Presence Inventory (Geller & Greenberg, 2010)
          </p>
        </div>
      </div>
    )
  }

  // Tool interface
  return (
    <div className="min-h-screen bg-[#F5F0EB] font-sans -mt-20">
      <div className="pt-28 pb-16 px-6">
        <div className="max-w-[560px] mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl text-[#2D2D2D]">Therapeutic Presence Self-Reflection</h1>
          </div>

          {/* Progress */}
          {currentSectionIndex >= 0 && currentSectionIndex < 4 && (
            <div className="mb-8">
              {/* Section dots */}
              <div className="flex justify-center gap-2 mb-3">
                {['immersion', 'attunement', 'emotional', 'focus'].map((cat, i) => (
                  <div
                    key={cat}
                    className={`w-10 h-1.5 rounded-full transition-all ${
                      currentSectionIndex > i ? 'bg-[#4A7C7C]' :
                      currentSectionIndex === i ? 'bg-[#4A7C7C]/60' : 'bg-[#E8E0D8]'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-center text-[#999]">
                Section {currentSectionIndex + 1} of 4
              </p>
            </div>
          )}

          {/* Slide Card */}
          <div
            className={`bg-white rounded-2xl shadow-[0_2px_24px_rgba(0,0,0,0.06)] p-8 transition-opacity duration-150 ${
              animating ? 'opacity-50' : 'opacity-100'
            }`}
          >
            {renderSlide()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <div>
              {canGoBack() && (
                <button
                  onClick={goBack}
                  className="text-sm text-[#7A7A7A] hover:text-[#4A7C7C] transition-all"
                >
                  ← Back
                </button>
              )}
            </div>

            <div>
              {canProceed() && (
                <button
                  onClick={nextSlide}
                  className="px-8 py-3 rounded-full font-semibold text-sm bg-[#4A7C7C] text-white hover:bg-[#2E5B5B] hover:-translate-y-0.5 transition-all"
                >
                  {currentSlide === slides.length - 1 ? 'See My Results →' : 'Continue →'}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
